<template>
  <div class="form-container">
    <el-form
      :inline="true"
      label-position="left"
      class="form-row"
      size="small"
      :label-width="lan == 'zh-CN' && '100px'"
    >
      <!-- 控制显示的表单项：展开时显示全部，收起时显示前 limitCount 个 -->
      <div
        class="form-col"
        v-for="(item, index) in displayedParams"
        :key="index"
      >
        <el-form-item :label="`${item.label}: `">
          <component
            v-if="item.type"
            :is="item.type"
            v-model="query[item.key]"
            :param="item"
          ></component>
          <el-input
            v-else
            v-model.trim="query[item.key]"
            :placeholder="item.placeholder || item.label"
            clearable
          />
        </el-form-item>
      </div>

      <!-- 搜索/清空按钮组（始终显示） -->
      <div class="form-col">
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="searchData">{{
            $t("elp.filter.search")
          }}</el-button>
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-delete" @click="clearData">{{
            $t("elp.filter.clear")
          }}</el-button>
        </el-form-item>
      </div>

      <!-- 新增按钮 -->
      <div class="form-col" v-if="add">
        <el-form-item>
          <el-button type="primary" icon="el-icon-plus" @click="addNew">{{
            $t("elp.filter.add")
          }}</el-button>
        </el-form-item>
      </div>

      <!-- 展开/收起按钮（当总表单项数量大于限制数量时显示） -->
      <div class="form-col" v-if="showExpandBtn">
        <el-form-item>
          <el-button type="text" @click="toggleExpand">
            <i :class="isExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
            {{ isExpanded ? $t("elp.filter.collapse") : $t("elp.filter.expand") }}
          </el-button>
        </el-form-item>
      </div>

      <slot name="handle"></slot>
    </el-form>
  </div>
</template>

<script>
import { reactive, ref, computed } from "@vue/reactivity";
import {
  Input,
  Select,
  SelectGroup,
  Date,
  Time,
} from "./formItem.js";
import { useStore } from "vuex";

export default {
  components: {
    Input,
    Select,
    SelectGroup,
    Date,
    Time,
  },
  props: {
    params: {
      type: Array,
      required: true,
    },
    add: {
      type: Boolean,
      default: false,
    },
    // 收起时最多显示的表单项数量（可根据每行能放几个估算，默认6个）
    collapseLimit: {
      type: Number,
      default: 11,
    },
    // 默认是否展开（false=收起）
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const store = useStore();
    const lan = store.state.lang;
    const query = reactive({});

    // 展开/收起状态
    const isExpanded = ref(props.defaultExpanded);

    // 初始化 query 默认值
    props.params.forEach((param) => {
      query[param.key] = param.value;
    });

    // 判断是否需要显示展开/收起按钮（表单项数量 + 固定按钮组数量 > 限制数量）
    // 固定按钮组包含了：搜索清空按钮、新增按钮（如果存在）、展开收起按钮本身
    // 为了简洁，当 params 长度超过 collapseLimit 时即显示按钮
    const showExpandBtn = computed(() => {
      return props.params.length > props.collapseLimit;
    });

    // 实际显示的表单项列表
    const displayedParams = computed(() => {
      if (!showExpandBtn.value || isExpanded.value) {
        // 不需要折叠 或 已展开：显示全部
        return props.params;
      } else {
        // 收起状态：只显示前 collapseLimit 个
        return props.params.slice(0, props.collapseLimit);
      }
    });

    // 切换展开/收起
    function toggleExpand() {
      isExpanded.value = !isExpanded.value;
    }

    function searchData() {
      console.log("searchData", query);
      context.emit("search", query);
    }

    function clearData() {
      for (let item in query) {
        if (Object.prototype.hasOwnProperty.call(query, item)) {
          query[item] = "";
        }
      }
    }

    function addNew() {
      context.emit("add");
    }

    return {
      query,
      searchData,
      clearData,
      addNew,
      lan,
      isExpanded,
      showExpandBtn,
      displayedParams,
      toggleExpand,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-container {
  padding: 20px 15px 0;
  border-color: #fff;
  border-radius: 5px;
  background-color: #fff;
  z-index: 99;
  position: relative;
}
.form-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}
:deep(.form-col) {
  flex: 0 1 auto;
  margin-right: 20px;
}
.form-col:last-child {
  margin-right: 0;
}
:deep(.form-col .el-form-item) {
  margin-bottom: 15px !important;
}
:deep(.form-col .el-form-item__label) {
  text-align: end;
}

:deep(.form-col .el-form-item__content) {
  max-width: 350px !important;
  overflow: hidden;
  .el-input {
    width: 100% !important;
  }
  .el-date-editor {
    width: 250px;
  }
  .el-range-editor {
    width: 350px;
  }
}

/* 可选：增加展开收起按钮的过渡效果（视觉优化） */
.expand-transition {
  transition: all 0.3s ease;
}
</style>